/**
 * API service for communicating with Travelers API Backend
 * Image processing endpoints for passport photos
 * 
 * API Documentation: https://travaliing-babaaa-2926a70935bd.herokuapp.com/docs
 * 
 * Available endpoints:
 * - POST /api/v1/image/process - Process image with background options
 * - POST /api/v1/image/auto-process - Auto process with AI crop/resize
 * - GET /api/v1/image/download/{filename} - Download in different formats
 * - GET /api/v1/image/print/layouts - Get available print layouts
 * - GET /api/v1/image/print/images - Get list of output images
 * - POST /api/v1/image/print/create - Create print layout PDF
 * - GET /api/v1/image/passport-countries - Get passport photo sizes by country
 */
import axios from 'axios';

// Production Backend URL - Traveling BABA API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://travaliing-babaaa-2926a70935bd.herokuapp.com';

// Create axios instance with configuration for image processing
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 2 minutes timeout for image processing (larger files)
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Log requests in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error('[API Error]', error.response?.status, error.message);
    }
    return Promise.reject(error);
  }
);

/**
 * Process image with background options
 * POST /api/v1/image/process
 */
export const processImage = async (formData) => {
  try {
    const response = await api.post('/api/v1/image/process', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to process image');
  }
};

/**
 * Auto process photo with crop, resize, and background removal
 * POST /api/v1/image/auto-process
 */
export const autoProcessImage = async (formData) => {
  try {
    const response = await api.post('/api/v1/image/auto-process', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to auto-process image');
  }
};

/**
 * Download image in different formats
 * GET /api/v1/image/download/{filename}
 * @param {string} filename - Image filename
 * @param {string} format - Output format: jpg, png, pdf
 * @param {number} quality - JPEG quality (1-100)
 * @param {string} pageSize - PDF page size: A4, Letter
 */
export const downloadImage = async (filename, format = 'png', quality = 95, pageSize = 'A4') => {
  try {
    const response = await api.get(`/api/v1/image/download/${filename}`, {
      params: { format, quality, page_size: pageSize },
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to download image');
  }
};

/**
 * Get available print layouts
 * GET /api/v1/image/print/layouts
 */
export const getPrintLayouts = async () => {
  try {
    const response = await api.get('/api/v1/image/print/layouts');
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch print layouts');
  }
};

/**
 * Get list of output images
 * GET /api/v1/image/print/images
 */
export const getOutputImages = async () => {
  try {
    const response = await api.get('/api/v1/image/print/images');
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch output images');
  }
};

/**
 * Create print layout PDF
 * POST /api/v1/image/print/create
 */
export const createPrintLayout = async (imageFilenames, layout = '2x2', pageSize = 'A4', spacing = 10) => {
  try {
    const formData = new FormData();
    formData.append('image_filenames', imageFilenames.join(','));
    formData.append('layout', layout);
    formData.append('page_size', pageSize);
    formData.append('spacing', spacing.toString());

    const response = await api.post('/api/v1/image/print/create', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to create print layout');
  }
};

/**
 * Get all passport photo country sizes
 * GET /api/v1/image/passport-countries
 */
export const getPassportCountries = async () => {
  try {
    const response = await api.get('/api/v1/image/passport-countries');
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch passport countries');
  }
};

/**
 * Get passport photo size for specific country
 * GET /api/v1/image/passport-countries/{country_name}
 */
export const getPassportCountrySize = async (countryName) => {
  try {
    const response = await api.get(`/api/v1/image/passport-countries/${encodeURIComponent(countryName)}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch country size');
  }
};

/**
 * Get full URL for output image
 * @param {string} imagePath - Relative path from API response
 * @returns {string} Full URL
 */
export const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  
  // If already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // Remove leading slash if present and construct full URL
  const path = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
  return `${API_BASE_URL}/${path}`;
};

/**
 * Get the API base URL
 * @returns {string} Base URL
 */
export const getApiBaseUrl = () => API_BASE_URL;

/**
 * Extract filename from image path
 * @param {string} imagePath - Full path or URL
 * @returns {string} Filename only
 */
export const getFilenameFromPath = (imagePath) => {
  if (!imagePath) return null;
  const parts = imagePath.split('/');
  return parts[parts.length - 1];
};

/**
 * Handle API errors consistently
 */
const handleApiError = (error, defaultMessage) => {
  if (error.response) {
    // Server responded with error
    throw new Error(
      error.response.data?.detail ||
      error.response.data?.message ||
      defaultMessage
    );
  } else if (error.request) {
    // No response received
    throw new Error('No response from server. Please check your connection.');
  } else {
    // Error setting up request
    throw new Error(error.message || defaultMessage);
  }
};

/**
 * Download blob as file
 * @param {Blob} blob - File blob
 * @param {string} filename - Desired filename
 */
export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export default api;
